import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  DeleteOutline,
  DownloadOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LatestUploadCard from "../LatestUploadCard";

const API = process.env.REACT_APP_BACKEND_URL;

const HEADERS = [
  "Part Code",
  "Part Description",
  "Project / Family",
  "Commodity",
  "Class",
  "Import / Local",
  "Bottleneck",
  "Use Level",
  "UoM",
  "Supplier",
  "Supplier Capacity / Month",
  "Supplier",
  "Supplier Capacity / Month",
  "TAT / Lead Time (days) — manual fallback",
  "MOQ",
];

const normalize = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return value;
  }

  const number = Number(String(value).replace(/,/g, "").trim());

  if (Number.isNaN(number)) {
    return null;
  }

  return number;
};

const findHeaderRow = (rows) => {
  for (let i = 0; i < rows.length; i++) {
    const headers = rows[i].map(normalize);

    if (
      headers.includes("Part Code") &&
      headers.includes("Part Description") &&
      headers.includes("Supplier")
    ) {
      return i;
    }
  }

  return -1;
};

const UploadPartMaster = () => {
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentRows, setCurrentRows] = useState([]);
  const [currentLoading, setCurrentLoading] = useState(true);
  const [currentError, setCurrentError] = useState("");

  const navigate = useNavigate();

  /*
   * Load the Parts Master currently stored
   */
  const loadCurrent = async () => {
    const user = JSON.parse(localStorage.getItem("user") || "null");

    try {
      setCurrentLoading(true);
      setCurrentError("");

      const response = await axios.get(`${API}/part-master`, {
        headers: {
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });

      setCurrentRows(
        (response.data || []).map((row, index) => ({
          ...row,
          id: row.partMasterId || index + 1,
        })),
      );
    } catch (err) {
      setCurrentRows([]);
      setCurrentError(
        err?.response?.data?.Exception ||
          err?.response?.data?.message ||
          "Unable to load the current Parts Master.",
      );
    } finally {
      setCurrentLoading(false);
    }
  };

  useEffect(() => {
    loadCurrent();
  }, []);

  const currentUploadedAt = currentRows.reduce(
    (latest, row) =>
      row.uploadedAt && (!latest || new Date(row.uploadedAt) > new Date(latest))
        ? row.uploadedAt
        : latest,
    null,
  );

  const currentStats = [
    { label: "Parts", value: currentRows.length },
    {
      label: "Suppliers",
      value: new Set(
        currentRows
          .flatMap((row) => [row.supplier1, row.supplier2])
          .map(normalize)
          .filter(Boolean),
      ).size,
    },
    {
      label: "Commodities",
      value: new Set(currentRows.map((row) => normalize(row.commodity)).filter(Boolean)).size,
    },
    {
      label: "Without lead time",
      value: currentRows.filter((row) => !Number(row.leadTimeDays)).length,
    },
  ];

  /*
   * Download Excel template
   */
  const downloadTemplate = () => {
    setError("");
    setSuccess("");

    const sheet = XLSX.utils.aoa_to_sheet([HEADERS]);

    sheet["!cols"] = HEADERS.map((header) => ({
      wch: header === "Part Description" ? 40 : header === "Supplier" ? 35 : 20,
    }));

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, sheet, "Parts Master");

    XLSX.writeFile(workbook, "ProcureTrack_Parts_Master_Template.xlsx");

    setSuccess("Parts Master template downloaded.");
  };

  /*
   * Read Excel file
   */
  const parseFile = async (selectedFile) => {
    const buffer = await selectedFile.arrayBuffer();

    const workbook = XLSX.read(buffer, {
      type: "array",
      raw: true,
    });

    if (!workbook.SheetNames.length) {
      throw new Error("The Excel file has no sheets.");
    }

    const worksheet = workbook.Sheets[workbook.SheetNames[0]];

    const data = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      defval: "",
      raw: true,
    });

    /*
     * Find header row.
     * This also means Sr. No. can exist above/before
     * the actual headers and will be ignored.
     */
    const headerRowIndex = findHeaderRow(data);

    if (headerRowIndex === -1) {
      throw new Error("Parts Master header row was not found.");
    }

    const headers = data[headerRowIndex].map(normalize);

    const dataRows = data.slice(headerRowIndex + 1);

    /*
     * Find column positions
     */
    const partCodeIndex = headers.indexOf("Part Code");

    const descriptionIndex = headers.indexOf("Part Description");

    const projectIndex = headers.indexOf("Project / Family");

    const commodityIndex = headers.indexOf("Commodity");

    const classIndex = headers.indexOf("Class");

    const importLocalIndex = headers.indexOf("Import / Local");

    const bottleneckIndex = headers.indexOf("Bottleneck");

    const useLevelIndex = headers.indexOf("Use Level");

    const uomIndex = headers.indexOf("UoM");

    const leadTimeIndex = headers.indexOf(
      "TAT / Lead Time (days) — manual fallback",
    );

    const moqIndex = headers.indexOf("MOQ");

    /*
     * There will be two Supplier columns
     * and two Capacity columns.
     */
    const supplierIndexes = [];
    const capacityIndexes = [];

    headers.forEach((header, index) => {
      if (header === "Supplier") {
        supplierIndexes.push(index);
      }

      if (header === "Supplier Capacity / Month") {
        capacityIndexes.push(index);
      }
    });

    /*
     * Make sure two supplier columns exist.
     */
    if (supplierIndexes.length < 2) {
      throw new Error("The Excel file must contain two Supplier columns.");
    }

    if (capacityIndexes.length < 2) {
      throw new Error(
        "The Excel file must contain two Supplier Capacity / Month columns.",
      );
    }

    /*
     * Keep one row per Part Code.
     */
    const grouped = new Map();

    dataRows.forEach((row) => {
      if (!row || row.every((value) => normalize(value) === "")) {
        return;
      }

      /*
       * Sr. No. is intentionally not read.
       */
      const partCode = normalize(row[partCodeIndex]);

      if (!partCode) {
        return;
      }

      if (!grouped.has(partCode)) {
        grouped.set(partCode, {
          partCode,
          partDescription: normalize(row[descriptionIndex]),

          projectFamily: normalize(row[projectIndex]),

          commodity: normalize(row[commodityIndex]),

          className: normalize(row[classIndex]),

          importLocal: normalize(row[importLocalIndex]),

          bottleneck: normalize(row[bottleneckIndex]),

          useLevel: normalize(row[useLevelIndex]),

          uom: normalize(row[uomIndex]),

          supplier1: "",
          supplier1CapacityPerMonth: null,

          supplier2: "",
          supplier2CapacityPerMonth: null,

          leadTimeDays: toNumber(row[leadTimeIndex]),

          moq: toNumber(row[moqIndex]),
        });
      }

      const part = grouped.get(partCode);

      /*
       * Fill missing normal values
       * if duplicate Part Code exists.
       */

      if (!part.partDescription) {
        part.partDescription = normalize(row[descriptionIndex]);
      }

      if (!part.projectFamily) {
        part.projectFamily = normalize(row[projectIndex]);
      }

      if (!part.commodity) {
        part.commodity = normalize(row[commodityIndex]);
      }

      if (!part.className) {
        part.className = normalize(row[classIndex]);
      }

      if (!part.importLocal) {
        part.importLocal = normalize(row[importLocalIndex]);
      }

      if (!part.bottleneck) {
        part.bottleneck = normalize(row[bottleneckIndex]);
      }

      if (!part.useLevel) {
        part.useLevel = normalize(row[useLevelIndex]);
      }

      if (!part.uom) {
        part.uom = normalize(row[uomIndex]);
      }

      const leadTime = toNumber(row[leadTimeIndex]);

      if (part.leadTimeDays === null && leadTime !== null) {
        part.leadTimeDays = leadTime;
      }

      const moq = toNumber(row[moqIndex]);

      if (part.moq === null && moq !== null) {
        part.moq = moq;
      }

      /*
       * Supplier 1
       */
      const supplier1 = normalize(row[supplierIndexes[0]]);

      if (!part.supplier1 && supplier1 && supplier1 !== "-") {
        part.supplier1 = supplier1;

        part.supplier1CapacityPerMonth = toNumber(row[capacityIndexes[0]]);
      }

      /*
       * Supplier 2
       */
      const supplier2 = normalize(row[supplierIndexes[1]]);

      if (!part.supplier2 && supplier2 && supplier2 !== "-") {
        part.supplier2 = supplier2;

        part.supplier2CapacityPerMonth = toNumber(row[capacityIndexes[1]]);
      }
    });

    const result = Array.from(grouped.values());

    if (!result.length) {
      throw new Error("No Parts Master data was found in the Excel file.");
    }

    return result;
  };

  /*
   * Excel selection
   */
  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setRows([]);
    setFile(null);

    try {
      const fileName = selectedFile.name.toLowerCase();

      if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".xls")) {
        throw new Error("Please upload an Excel file (.xlsx or .xls).");
      }

      const parsedRows = await parseFile(selectedFile);

      setFile(selectedFile);

      setRows(
        parsedRows.map((row, index) => ({
          ...row,
          id: index + 1,
        })),
      );

      setSuccess(`${parsedRows.length} material records loaded for preview.`);
    } catch (err) {
      setError(err?.message || "Unable to read the Parts Master Excel file.");
    } finally {
      setLoading(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  /*
   * Submit to backend
   */
  const handleSubmit = async () => {
    if (!file || rows.length === 0) {
      setError("Please upload a Parts Master file first.");

      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "null");

    if (!user?.accessToken) {
      setError("You are not logged in.");

      return;
    }

    setUploading(true);
    setError("");
    setSuccess("");

    try {
      /*
       * id is only for DataGrid.
       * Do not send it to backend.
       */
      const payload = rows.map(({ id, ...row }) => row);

      const response = await axios.post(`${API}/part-master/upload`, payload, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
          "Content-Type": "application/json",
        },
      });

      setSuccess(response.data || "Parts Master uploaded successfully.");

      navigate("/admin");
    } catch (err) {
      setError(
        err?.response?.data?.Exception ||
          err?.response?.data?.message ||
          err?.message ||
          "Parts Master upload failed.",
      );
    } finally {
      setUploading(false);
    }
  };

  /*
   * Clear selected file
   */
  const clear = () => {
    setFile(null);
    setRows([]);
    setError("");
    setSuccess("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /*
   * DataGrid columns
   */
  const columns = useMemo(
    () => [
      {
        field: "partCode",
        headerName: "Part Code",
        minWidth: 130,
        flex: 1,
      },

      {
        field: "partDescription",
        headerName: "Part Description",
        minWidth: 250,
        flex: 1.8,
      },

      {
        field: "projectFamily",
        headerName: "Project / Family",
        minWidth: 140,
      },

      {
        field: "commodity",
        headerName: "Commodity",
        minWidth: 120,
      },

      {
        field: "className",
        headerName: "Class",
        minWidth: 80,
      },

      {
        field: "importLocal",
        headerName: "Import / Local",
        minWidth: 110,
      },

      {
        field: "bottleneck",
        headerName: "Bottleneck",
        minWidth: 110,
      },

      {
        field: "useLevel",
        headerName: "Use Level",
        minWidth: 110,
      },

      {
        field: "uom",
        headerName: "UOM",
        minWidth: 80,
      },

      {
        field: "supplier1",
        headerName: "Supplier 1",
        minWidth: 240,
        flex: 1.3,
      },

      {
        field: "supplier1CapacityPerMonth",
        headerName: "Supplier 1 Capacity",
        minWidth: 145,
      },

      {
        field: "supplier2",
        headerName: "Supplier 2",
        minWidth: 240,
        flex: 1.3,
      },

      {
        field: "supplier2CapacityPerMonth",
        headerName: "Supplier 2 Capacity",
        minWidth: 145,
      },

      {
        field: "leadTimeDays",
        headerName: "Lead Time (Days)",
        minWidth: 125,
      },

      {
        field: "moq",
        headerName: "MOQ",
        minWidth: 95,
      },
    ],
    [],
  );

  return (
    <Box sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#172033",
          }}
        >
          Parts Master
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Upload the Parts Master used for procurement planning.
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            whiteSpace: "pre-line",
          }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <LatestUploadCard
          title="Current Parts Master"
          uploadedAt={currentUploadedAt}
          stats={currentStats}
          loading={currentLoading}
          error={currentError}
          isEmpty={currentRows.length === 0}
          emptyMessage="No Parts Master has been uploaded yet."
          onRefresh={loadCurrent}
          rows={currentRows}
          columns={columns}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: "1px solid #e3e7ef",
          borderRadius: 2,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
          }}
        >
          Parts Master Excel
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.5,
            mb: 2,
          }}
        >
          Sr. No. is ignored. Supplier 1 and Supplier 2 are stored directly
          against the same Part Master record.
        </Typography>

        {file && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              fontWeight: 600,
            }}
          >
            Selected file: {file.name}
          </Typography>
        )}

        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept=".xlsx,.xls"
          onChange={handleFileChange}
        />

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={1}
        >
          <Button
            variant="outlined"
            startIcon={<DownloadOutlined />}
            onClick={downloadTemplate}
            disabled={loading || uploading}
          >
            Download Template
          </Button>

          <Button
            variant="outlined"
            startIcon={
              loading ? <CircularProgress size={18} /> : <UploadFileOutlined />
            }
            onClick={() => fileInputRef.current?.click()}
            disabled={loading || uploading}
          >
            {loading ? "Processing..." : "Choose Excel"}
          </Button>

          {rows.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={clear}
              disabled={uploading}
            >
              Clear
            </Button>
          )}
        </Stack>
      </Paper>

      {rows.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e3e7ef",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #e3e7ef",

              display: "flex",

              justifyContent: "space-between",

              alignItems: {
                xs: "flex-start",
                md: "center",
              },

              gap: 2,

              flexDirection: {
                xs: "column",
                md: "row",
              },
            }}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  Parts Master Preview
                </Typography>

                <Chip
                  size="small"
                  label={`${rows.length} materials`}
                  variant="outlined"
                />
              </Stack>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.4,
                }}
              >
                Review the data before replacing the current Parts Master.
              </Typography>
            </Box>

            <Button
              className="btn-contained"
              startIcon={
                uploading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <UploadFileOutlined />
                )
              }
              onClick={handleSubmit}
              disabled={uploading || loading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 1.5,
              }}
            >
              {uploading ? "Replacing Master..." : "Submit Parts Master"}
            </Button>
          </Box>

          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 25,
                  page: 0,
                },
              },
            }}
            sx={{
              border: 0,

              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f7f9fc",
                fontWeight: 700,
              },

              "& .MuiDataGrid-cell": {
                fontSize: "0.82rem",
              },
            }}
          />
        </Paper>
      )}

    </Box>
  );
};

export default UploadPartMaster;
